/**
 * What a country means to the software (#181): one frozen profile per
 * admitted jurisdiction, keyed by ISO 3166-1 alpha-2 code.
 *
 * Burkina Faso reads every value from the constant it stands for, so those
 * constants stay the single source. A country other than Burkina Faso
 * enters `JURISDICTIONS` only with its sourced note in
 * `docs/fiscal/<iso>/README.md` (a test refuses an entry without it).
 * `TEST_JURISDICTIONS` holds the fixture `ZZ`, reachable only where a
 * caller passes it explicitly.
 *
 * Every period resolver reads the UTC day (`currencyDay`): a profile whose
 * zone is not UTC+0 all year is refused when the registry loads.
 *
 * The accounting chart family and the payroll and declaration packs are
 * named by string code: core never imports @efacture/accounting.
 */
import { ApplicationError } from "../errors/index.js";
import { IFU_PATTERN_BF } from "../constants/client-types.js";
import { DGI_PARAMETER_TABLES } from "../parametrage/dgi-tables.js";
import { GENERIC_PARAMETER_TABLES } from "../parametrage/generic-tables.js";
/**
 * `instant` read on the clock of `timeZone`, whatever the process zone.
 * Throws a RangeError on an invalid date or an unknown zone.
 */
export function zonedDateTimeParts(instant, timeZone) {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        hourCycle: "h23",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    }).formatToParts(instant);
    const part = (type) => parts.find((p) => p.type === type)?.value ?? "";
    return {
        year: part("year"),
        month: part("month"),
        day: part("day"),
        hour: part("hour"),
        minute: part("minute"),
        second: part("second"),
    };
}
/** Minutes `timeZone` is ahead of UTC at `instant`. */
export function utcOffsetMinutes(timeZone, instant) {
    const clock = zonedDateTimeParts(instant, timeZone);
    const local = Date.UTC(Number(clock.year), Number(clock.month) - 1, Number(clock.day), Number(clock.hour), Number(clock.minute));
    return Math.round((local - Math.floor(instant.getTime() / 60_000) * 60_000) / 60_000);
}
/**
 * Refuses a profile whose zone is not UTC+0 in January and in July: the
 * period resolvers take the UTC day, which is the zone's day only there.
 */
export function assertUtcZone(profile) {
    const year = new Date().getUTCFullYear();
    for (const month of [0, 6]) {
        const offset = utcOffsetMinutes(profile.timeZone, new Date(Date.UTC(year, month, 15, 12)));
        if (offset !== 0) {
            throw new ApplicationError(`Juridiction ${profile.code} : le fuseau ${profile.timeZone} n'est pas UTC+0 toute l'année ; ` +
                "le calendrier des périodes est lu au jour UTC.", "JURIDICTION_FUSEAU_NON_ADMIS", 500);
        }
    }
}
function admitted(profile) {
    assertUtcZone(profile);
    return Object.freeze(profile);
}
const BF = admitted({
    code: "BF",
    name: "Burkina Faso",
    timeZone: "Africa/Ouagadougou",
    currencyOrigin: Object.freeze({
        currency: "XOF",
        legalReference: "Franc CFA (UEMOA), unité monétaire ayant cours légal au Burkina Faso",
    }),
    taxScheme: "BF_DGI",
    fiscalParameters: Object.freeze(["STAMP_DUTY_SCALE"]),
    documentTypes: Object.freeze(["FV", "FT", "FA", "EV", "ET", "EA"]),
    parameterFloor: DGI_PARAMETER_TABLES,
    issuerMentions: "BF_SFE",
    taxIdentifier: Object.freeze({ label: "IFU", pattern: IFU_PATTERN_BF }),
    rounding: "CENTIME_HALF_AWAY_PER_LINE",
    certifier: Object.freeze({ device: "SECeF", mcfProtocol: "SECEF BF ST 1.0" }),
    taxpayerCategories: Object.freeze(["DGE", "DME", "PE", "MICRO"]),
    chartFamily: "OHADA",
    payrollPack: "BF",
    declarationsPack: "BF",
    sources: "docs/fiscal/",
});
/** The admitted jurisdictions. */
export const JURISDICTIONS = Object.freeze({ BF });
/** The jurisdiction whose invoices the SECeF certifier (MCF, mcf-agent) signs. */
export const SECEF_JURISDICTION = BF.code;
/** Zone every date and hour of a SECeF document is printed in. */
export const SECEF_TIME_ZONE = BF.timeZone;
/**
 * Test fixture: a GENERIC_VAT profile under the user-assigned code `ZZ`.
 * Reachable only when passed explicitly (unit tests) or when the API is
 * started with EFACTURE_TEST_JURISDICTIONS=1, which it refuses in production.
 */
export const TEST_JURISDICTIONS = Object.freeze({
    ZZ: admitted({
        code: "ZZ",
        name: "Pays d'essai (ZZ)",
        timeZone: "UTC",
        currencyOrigin: Object.freeze({
            currency: "XOF",
            legalReference: "Juridiction d'essai : monnaie de la donnée de test",
        }),
        taxScheme: "GENERIC_VAT",
        fiscalParameters: Object.freeze([]),
        documentTypes: Object.freeze(["FV", "FA"]),
        parameterFloor: GENERIC_PARAMETER_TABLES,
        issuerMentions: "GENERIC_MINIMUM",
        taxIdentifier: Object.freeze({ label: "Identifiant fiscal", pattern: null }),
        rounding: "CENTIME_HALF_AWAY_PER_LINE",
        certifier: null,
        taxpayerCategories: Object.freeze([]),
        chartFamily: "OHADA",
        payrollPack: null,
        declarationsPack: null,
        sources: "packages/fiscal-core/src/jurisdictions/registry.ts",
    }),
});
/**
 * The profile of `code` among `registry` (the admitted jurisdictions by
 * default); refuses an unknown code.
 */
export function jurisdictionProfile(code, registry = JURISDICTIONS) {
    const profile = Object.prototype.hasOwnProperty.call(registry, code) ? registry[code] : undefined;
    if (!profile) {
        throw new ApplicationError(`Juridiction inconnue : « ${String(code)} »`, "JURIDICTION_INCONNUE", 400);
    }
    return profile;
}
/** True when `code` names a profile of `registry`. */
export function isKnownJurisdiction(code, registry = JURISDICTIONS) {
    return typeof code === "string" && Object.prototype.hasOwnProperty.call(registry, code);
}
/** True when `type` is an invoice type the profile offers. */
export function offersDocumentType(profile, type) {
    return profile.documentTypes.includes(type);
}
//# sourceMappingURL=registry.js.map